/**
 * GlobalRevisionsModal - Full modal overlay for viewing all global revisions
 * Following pattern from existing StoreDetailsModal.jsx
 */

import { useState, useEffect } from 'react';
import {
    X,
    Search,
    Filter,
    Calendar,
    ChevronDown,
    ChevronRight,
    RefreshCw,
    AlertTriangle,
    Info
} from 'lucide-react';
import GlobalRevisionCard from './GlobalRevisionCard.jsx';
import GlobalRevisionsService from '../../utils/globalRevisionsService.js';

export default function GlobalRevisionsModal({
    isOpen,
    onClose,
    revisions = [],
    loading = false,
    error = null,
    hasMore = false,
    onLoadMore = () => { },
    onSearch = () => { },
    onFilterChange = () => { },
    filters = {},
    selectedWeek = null,
    stats = {},
    testMode = true
}) {
    const [searchTerm, setSearchTerm] = useState(filters.search || '');
    const [showFilters, setShowFilters] = useState(false);
    const [groupBy, setGroupBy] = useState('date'); // 'date', 'client', 'type'

    // Add modal-open class to body when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
            return () => {
                document.body.classList.remove('modal-open');
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Debounce search
        if (handleSearchChange.timeoutId) {
            clearTimeout(handleSearchChange.timeoutId);
        }

        handleSearchChange.timeoutId = setTimeout(() => {
            onSearch(value);
        }, 300);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        onFilterChange({ [key]: value });
    };

    // Group revisions based on selected grouping
    const getGroupedRevisions = () => {
        switch (groupBy) {
            case 'client':
                return GlobalRevisionsService.groupRevisionsByClient(revisions);
            case 'type':
                return revisions.reduce((acc, revision) => {
                    const type = revision.type || 'general';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(revision);
                    return acc;
                }, {});
            case 'date':
            default:
                // Group by week
                return revisions.reduce((acc, revision) => {
                    const date = new Date(revision.date);
                    const weekStart = new Date(date);
                    weekStart.setDate(date.getDate() - date.getDay());
                    const weekKey = weekStart.toISOString().split('T')[0];
                    const weekLabel = `Week of ${weekStart.toLocaleDateString()}`;

                    if (!acc[weekLabel]) acc[weekLabel] = [];
                    acc[weekLabel].push(revision);
                    return acc;
                }, {});
        }
    };

    const groupedRevisions = getGroupedRevisions();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
                className="bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'calc(100vw - 2rem)',
                    maxWidth: '1200px',
                    height: '90vh',
                    minHeight: '600px'
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-slate-50 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900">Global Revisions</h2>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                                {selectedWeek && (
                                    <span className="font-medium">
                                        📅 {selectedWeek.description} ({selectedWeek.start} to {selectedWeek.end})
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Stats */}
                        {stats.totalFound !== undefined && (
                            <div className="text-sm text-slate-600">
                                <span className="font-medium">{stats.totalFound}</span> revisions found
                            </div>
                        )}

                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Search and Filters Bar */}
                <div className="p-4 border-b border-slate-200 bg-white flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search revisions by client or content..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                        </div>

                        {/* Group By */}
                        <select
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value)}
                            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="date">Group by Date</option>
                            <option value="client">Group by Client</option>
                            <option value="type">Group by Type</option>
                        </select>

                        {/* Filter Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`
                                flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
                                ${showFilters
                                    ? 'bg-orange-50 border-orange-200 text-orange-700'
                                    : 'border-slate-300 hover:bg-slate-50'
                                }
                            `}
                        >
                            <Filter size={16} />
                            Filters
                            {showFilters ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                    </div>

                    {/* Expanded Filters */}
                    {showFilters && (
                        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Active Only Filter */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="activeOnly"
                                        checked={filters.activeOnly !== false}
                                        onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                                        className="rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                                    />
                                    <label htmlFor="activeOnly" className="text-sm text-slate-700">
                                        Active revisions only
                                    </label>
                                </div>

                                {/* Date From */}
                                <div>
                                    <label className="block text-sm text-slate-600 mb-1">From Date</label>
                                    <input
                                        type="date"
                                        value={filters.dateFrom || ''}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>

                                {/* Date To */}
                                <div>
                                    <label className="block text-sm text-slate-600 mb-1">To Date</label>
                                    <input
                                        type="date"
                                        value={filters.dateTo || ''}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 min-h-0">
                    {loading && revisions.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
                                </div>
                                <p className="text-slate-600 font-medium">Loading global revisions...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center max-w-md">
                                <AlertTriangle size={32} className="text-red-500 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">Error Loading Revisions</h3>
                                <p className="text-slate-600 mb-4">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    ) : revisions.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center max-w-md">
                                <Info size={32} className="text-slate-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">No Revisions Found</h3>
                                <p className="text-slate-600">
                                    {searchTerm
                                        ? `No revisions match your search for "${searchTerm}"`
                                        : "No global revisions are available for the selected period."
                                    }
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-6">
                            {/* Grouped Revisions */}
                            <div className="space-y-6">
                                {Object.entries(groupedRevisions).map(([groupKey, groupRevisions]) => (
                                    <div key={groupKey} className="space-y-4">
                                        {/* Group Header */}
                                        <div className="flex items-center gap-3 pb-3 border-b-2 border-slate-200">
                                            <h3 className="text-lg font-bold text-slate-900">{groupKey}</h3>
                                            <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full">
                                                {groupRevisions.length} revision{groupRevisions.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>

                                        {/* Group Revisions */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {groupRevisions.map((revision, index) => (
                                                <GlobalRevisionCard
                                                    key={revision.id || `${groupKey}-${index}`}
                                                    revision={revision}
                                                    variant="default"
                                                    showClient={groupBy !== 'client'}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Load More Button */}
                            {hasMore && (
                                <div className="flex justify-center mt-8">
                                    <button
                                        onClick={onLoadMore}
                                        disabled={loading}
                                        className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Loading more...
                                            </>
                                        ) : (
                                            <>
                                                View More Revisions
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with stats */}
                {stats.totalActive !== undefined && (
                    <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                        <div className="flex items-center justify-between text-sm text-slate-600">
                            <div className="flex items-center gap-4">
                                <span>
                                    <strong>{stats.totalActive}</strong> active revisions
                                </span>
                                <span>
                                    <strong>{stats.totalClients}</strong> clients
                                </span>
                            </div>
                            <div>
                                Last updated: {new Date().toLocaleTimeString()}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
